import React from 'react';

import { faClose, faEquals, faPlus, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { ReactComponent as LockSVG } from 'assets/svg/lock.svg';
import { ReactComponent as UnionIconSVG } from 'assets/svg/union-icon.svg';
import { ReactComponent as UnlockSVG } from 'assets/svg/unlocked.svg';
import { ButtonSizeVariants, ButtonVariants } from 'constants/common';
import { DAY_FORMAT_WITH_SLASHES } from 'constants/global';
import {
  FILTER_CONDITIONS_FOR_BOOLEANS,
  FILTER_CONDITIONS_FOR_NUMBERS,
  FILTER_CONDITIONS_FOR_STRINGS,
  FilterItem,
  SortDirection,
} from 'constants/reports-templates';
import { JoinRow } from 'interfaces/report-templates.interface';
import { Button } from 'shared-components';
type Props = {
  title: string;
  onClick?: () => void;
  joins?: JoinRow[];
  grouping?: { table: string; column: string } | null;
  aggregates?: { func: string; column?: string }[];
  filters?: FilterItem[];
  sorting?: { table: string; column: string; direction: SortDirection } | null;
  onDeleteJoin?: (id: number) => void;
  onDeleteGroupings?: () => void;
  onDeleteAggregate?: (index: number) => void;
  onDeleteFilter?: (id: string) => void;
  onDeleteSorting?: () => void;
};

const CreateReportTemplateFilterItem: React.FC<Props> = ({
  title,
  onClick,
  joins = [],
  grouping,
  aggregates = [],
  filters = [],
  sorting,
  onDeleteJoin,
  onDeleteGroupings,
  onDeleteAggregate,
  onDeleteFilter,
  onDeleteSorting,
}) => {
  const { t } = useTranslation();

  const getConditionLabelById = (id: number | string | null): string => {
    const allConditions = [
      ...FILTER_CONDITIONS_FOR_STRINGS,
      ...FILTER_CONDITIONS_FOR_NUMBERS,
      ...FILTER_CONDITIONS_FOR_BOOLEANS,
    ];

    return allConditions.find((condition) => condition.id === id)?.label ?? String(id ?? '');
  };

  return (
    <div className="max-w-full overflow-x-hidden">
      <div className="border-t border-neutral-200 dark:border-neutral-800 py-5 flex items-center justify-between hover:text-orange-500">
        <span className="font-medium text-sm">{t(title)}</span>
        <div
          onClick={onClick}
          className="w-6 h-6 hover:border-1 hover:border-neutral-200 dark:hover:border-neutral-800 flex items-center justify-center cursor-pointer rounded-sm hover:bg-neutral-100 dark:hover:bg-neutral-900"
        >
          <FontAwesomeIcon icon={faPlus} className="text-neutral-500" />
        </div>
      </div>

      {joins.length > 0 && (
        <div className="mt-2 ml-1 flex flex-col gap-2">
          {joins.map((join, index) => (
            <div key={index} className="flex flex-col gap-2 text-sm mb-5">
              <div className="flex justify-between w-full">
                <div className="flex items-center mb-3">
                  <UnionIconSVG className="fill-neutral-500 w-4 h-4 mr-1" />
                  <p className="text-neutral-600 dark:text-neutral-400">{t('Join')}</p>
                </div>
                <Button
                  type="button"
                  className="w-6"
                  onClick={() => onDeleteJoin?.(join.id)}
                  variant={ButtonVariants.DESTRUCTIVE}
                  size={ButtonSizeVariants.SM}
                >
                  <FontAwesomeIcon icon={faTrashCan} />
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-1">
                <span className="report-templates-filters-styles">{join.leftTable}</span>
                <FontAwesomeIcon icon={faPlus} className="text-black-900 dark:text-white-00 w-3 h-3" />
                <span className="report-templates-filters-styles">{join.rightTable}</span>
                <p className="text-black-900 dark:text-white-00">on</p>
                <span className="report-templates-filters-styles">{join.leftColumn}</span>
                <FontAwesomeIcon icon={faEquals} className="text-black-900 dark:text-white-00 w-3 h-3" />
                <span className="report-templates-filters-styles">{join.rightColumn}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {grouping && (
        <div className="my-1 mr-1 flex justify-between items-center mb-6">
          <div className="flex flex-wrap gap-2 break-words w-full">
            <span className="report-templates-filters-styles">{grouping.table}</span>
            <span className="report-templates-filters-styles">{grouping.column}</span>
          </div>
          <FontAwesomeIcon
            icon={faClose}
            onClick={onDeleteGroupings}
            className="text-neutral-500 w-4 h-4 cursor-pointer"
          />
        </div>
      )}

      {aggregates?.length > 0 && (
        <div className="my-1 mr-1  mb-6">
          {aggregates.map((aggregate, index) => (
            <div key={index} className="flex justify-between items-center mb-3">
              <div className="flex flex-wrap gap-2 break-words w-full">
                <span className="report-templates-filters-styles">{aggregate.func}</span>
                {aggregate.column && <span className="report-templates-filters-styles">{aggregate.column}</span>}
              </div>
              <FontAwesomeIcon
                icon={faClose}
                onClick={() => onDeleteAggregate?.(index)}
                className="text-neutral-500 w-4 h-4 cursor-pointer"
              />
            </div>
          ))}
        </div>
      )}

      {filters?.length > 0 && (
        <div className="my-1 mr-1  mb-6">
          {filters.map((filter, index) => (
            <div key={index} className="flex justify-between items-center mb-3">
              <div className="flex flex-wrap gap-2 break-words w-full">
                {filter.locked ? <LockSVG className="w-4" /> : <UnlockSVG className="w-4" />}
                {filter.operator ? <span className="report-templates-filters-styles">{filter.operator}</span> : null}
                <span className="report-templates-filters-styles">{filter.column}</span>
                {filter.condition && (
                  <span className="report-templates-filters-styles">{getConditionLabelById(filter.condition)}</span>
                )}
                {filter.value && (
                  <span className="report-templates-filters-styles">
                    {filter.value instanceof Date
                      ? format(filter.value, DAY_FORMAT_WITH_SLASHES)
                      : String(filter.value)}
                  </span>
                )}
              </div>
              <FontAwesomeIcon
                icon={faClose}
                onClick={() => onDeleteFilter?.(filter.id)}
                className="text-neutral-500 w-4 h-4 cursor-pointer"
              />
            </div>
          ))}
        </div>
      )}

      {sorting && (
        <div className="my-1 mr-1 flex justify-between items-center mb-6">
          <div className="flex flex-wrap gap-2 break-words w-full">
            <span className="report-templates-filters-styles">{sorting.column}</span>
            <span className="report-templates-filters-styles">
              {sorting.direction === 'asc' || 1 ? 'Ascending' : 'Descending'}
            </span>
          </div>
          <FontAwesomeIcon
            icon={faClose}
            onClick={onDeleteSorting}
            className="text-neutral-500 w-4 h-4 cursor-pointer"
          />
        </div>
      )}
    </div>
  );
};

export default CreateReportTemplateFilterItem;
